import { useCallback, useEffect, useState } from 'react';
import { getMentionComposeHint, hasIncompleteMention } from './mentionEmail';

export type MentionComposeElement = HTMLInputElement | HTMLTextAreaElement;

export interface UseEmailMentionAssistOptions {
  enabled?: boolean;
  /**
   * Controlled compose value. When set, the hint is derived synchronously from this
   * string so we never setState on input before the parent commits the new value
   * (which would reset a controlled textarea and swallow characters like "@").
   */
  text?: string;
}

export function useEmailMentionAssist(
  element: MentionComposeElement | null,
  options: UseEmailMentionAssistOptions = {}
) {
  const { enabled = true, text } = options;
  const [listenerHint, setListenerHint] = useState<string | null>(null);

  const refreshListenerHint = useCallback(
    (value: string) => {
      if (!enabled) {
        setListenerHint(null);
        return;
      }
      setListenerHint(getMentionComposeHint(value));
    },
    [enabled]
  );

  const hintMessage =
    text !== undefined
      ? enabled
        ? getMentionComposeHint(text)
        : null
      : listenerHint;

  useEffect(() => {
    if (text !== undefined || !element || !enabled) {
      return;
    }

    const onInput = () => {
      // Run after DevExtreme/React update the field so we do not read stale text.
      queueMicrotask(() => refreshListenerHint(element.value));
    };

    refreshListenerHint(element.value);
    element.addEventListener('input', onInput);
    return () => element.removeEventListener('input', onInput);
  }, [element, enabled, refreshListenerHint, text]);

  const validateBeforeSend = useCallback(
    (value: string): string | null => {
      if (!enabled || !hasIncompleteMention(value)) return null;
      return 'Finish each mention from the picker or remove @ before sending.';
    },
    [enabled]
  );

  return {
    hintVisible: hintMessage !== null,
    hintMessage,
    validateBeforeSend
  };
}
